import React from "react";
import { Code2, Cpu, Database, Lock } from "lucide-react";

const techStack = [
  {
    icon: Lock,
    title: "ZK-Proofs",
    items: ["Zero-knowledge verification", "Privacy-preserving", "Fair execution guarantees"],
  },
  {
    icon: Database,
    title: "Celestia DA",
    items: ["Modular data availability", "Scalable storage", "Cost-effective consensus"],
  },
  {
    icon: Code2,
    title: "Light-Client First",
    items: ["Lightweight nodes", "Fast synchronization", "Accessible participation"],
  },
  {
    icon: Cpu,
    title: "Community Focus",
    items: ["Open governance", "Transparent operations", "User-friendly tools"],
  },
];

const TechnologySection = () => {
  return (
    <section className="py-20 relative">
      <div className="text-center mb-16">
        <h2
          className="text-5xl font-bold mb-4"
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "700",
            letterSpacing: "-0.02em",
          }}
        >
          Technology Stack
        </h2>
        <p
          className="text-xl max-w-2xl mx-auto"
          style={{
            color: "#94a3b8",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "400",
          }}
        >
          Combining advanced cryptography with community-first principles
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {techStack.map((tech, index) => (
          <div
            key={index}
            className="group p-6 rounded-2xl transition-all duration-300 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(51, 65, 85, 0.4) 0%, rgba(30, 41, 59, 0.6) 100%)",
              boxShadow: `
                0 4px 20px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 0 1px rgba(148, 163, 184, 0.2)
              `,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = `
                0 12px 35px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.15),
                0 0 0 1px rgba(203, 108, 230, 0.4),
                0 0 40px rgba(203, 108, 230, 0.3)
              `;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.boxShadow = `
                0 4px 20px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 0 1px rgba(148, 163, 184, 0.2)
              `;
            }}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
              style={{
                background: "linear-gradient(135deg, rgba(203, 108, 230, 0.4) 0%, transparent 100%)",
              }}
            />

            <div className="relative z-10">
              <div
                className="p-3 rounded-xl inline-block mb-4"
                style={{
                  background: "linear-gradient(135deg, rgba(203, 108, 230, 0.2) 0%, rgba(203, 108, 230, 0.1) 100%)",
                  boxShadow: "0 4px 15px rgba(203, 108, 230, 0.3)",
                }}
              >
                <tech.icon
                  className="w-6 h-6"
                  style={{
                    color: "#cb6ce6",
                    filter: "drop-shadow(0 0 10px rgba(203, 108, 230, 0.6))",
                  }}
                />
              </div>

              <h3
                className="text-lg font-semibold mb-3"
                style={{
                  color: "#f1f5f9",
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: "600",
                }}
              >
                {tech.title}
              </h3>

              <ul className="space-y-2">
                {tech.items.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-sm"
                    style={{
                      color: "#94a3b8",
                      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      fontWeight: "400",
                    }}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: "#cb6ce6",
                        boxShadow: "0 0 8px rgba(203, 108, 230, 0.6)",
                      }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div
        className="mt-16 p-8 rounded-2xl text-center"
        style={{
          background: "linear-gradient(135deg, rgba(203, 108, 230, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)",
          border: "1px solid rgba(203, 108, 230, 0.3)",
          boxShadow: `
            0 8px 30px rgba(203, 108, 230, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.1)
          `,
        }}
      >
        <h3
          className="text-3xl font-bold mb-4"
          style={{
            background: "linear-gradient(135deg, #cb6ce6 0%, #e879f9 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "700",
          }}
        >
          Join the Øfun Ecosystem
        </h3>
        <p
          className="text-lg mb-6 max-w-2xl mx-auto"
          style={{
            color: "#e2e8f0",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "400",
            lineHeight: "1.7",
          }}
        >
          Be part of the next generation of blockchain infrastructure. Build, scale, and innovate with Øfun.
        </p>
        <div className="flex gap-4 justify-center">
          <a
            href="https://github.com/zerofunxyz/Ofun"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 rounded-xl transition-all duration-300"
            style={{
              background: "linear-gradient(135deg, #cb6ce6 0%, #a855f7 100%)",
              color: "#ffffff",
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontWeight: "500",
              boxShadow: "0 4px 15px rgba(203, 108, 230, 0.4)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 25px rgba(203, 108, 230, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(203, 108, 230, 0.4)";
            }}
          >
            Get Started
          </a>
          <a
            href="https://x.com/zerofunxyz"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-3 rounded-xl transition-all duration-300"
            style={{
              background: "transparent",
              color: "#cb6ce6",
              border: "1px solid rgba(203, 108, 230, 0.4)",
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontWeight: "500",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.background = "rgba(203, 108, 230, 0.1)";
              e.currentTarget.style.borderColor = "rgba(203, 108, 230, 0.6)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(203, 108, 230, 0.4)";
            }}
          >
            Learn More
          </a>
        </div>
      </div>
    </section>
  );
}

export default TechnologySection;
